/**
 * "A scene never edits the inside of a reference" (SCENE_ROOT_SPEC §2.3,
 * "Editing").
 *
 * A placed document asset is `R > D.root > …`. The placement `R` is this
 * scene's own node; everything beneath it belongs to the referenced document
 * and is dropped on export. An edit that would land inside the subtree copies
 * the reference into the scene first (`TraceScene.unpackReference` with the
 * scene's folder), and then applies to nodes the scene owns. An edit to `R`
 * itself never copies.
 *
 * The rule is forward-only (§2.3, "Scope"): it governs a session where
 * `TraceScene.placementRuleActive` holds. A legacy session copies nothing:
 * the operations edit inside a reference there in place, as they do inside
 * any reference this rule does not copy.
 *
 * Only library references (`<api>/assets/<id>/file`) are copied: their
 * folder is what the unpack brings across.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { UnpackFolderTarget } from '../../core/TraceScene';
import { matchAssetFileUrlPath } from '../../content/assetRef';
import type { UndoCommand } from '../undo/UndoCommand';
import { CompositeCommand } from '../undo/CompositeCommand';
import { CreateObjectCommand } from '../undo/CreateObjectCommand';
import { DeleteObjectCommand } from '../undo/DeleteObjectCommand';
import { capture, descendantEntries, rollBack } from './steps';
import type { AuthoringPorts, Refusal } from './types';

/**
 * Whether `object` is a placement of a document: the asset's own root is its
 * child and owns the geometry, so geometry behaviors don't belong on it.
 * False for a file reference, which owns its file, and for an ordinary object.
 */
export function isDocumentPlacement(object: SceneObject | null | undefined): boolean {
    return !!object && object.referenceAncestor() === object;
}

/**
 * The library placement an edit to `object` must copy first, else null: the
 * OUTERMOST one above it. A reference nested in another is the outer one's
 * content, so it can only be unpacked once the outer one is.
 */
function referenceCopyTarget(object: SceneObject): SceneObject | null {
    if (!object.traceScene.placementRuleActive) return null;
    let outermost: SceneObject | null = null;
    for (let node = object.getParent(); node; node = node.getParent()) {
        if (node.referenceAncestor() === node && matchAssetFileUrlPath(node.src) !== null) outermost = node;
    }
    return outermost;
}

/**
 * Whether an edit to `object` lands inside a placed library reference and must
 * copy it first. False in a legacy session, which copies nothing.
 */
export function needsReferenceCopy(object: SceneObject | null | undefined): boolean {
    return !!object && referenceCopyTarget(object) !== null;
}

/** Why no edit may land on `object`: the host holds it read-only (`ports.readOnly`). */
export function readOnlyRefusal(object: SceneObject, ports: AuthoringPorts): Refusal | null {
    if (!ports.readOnly?.(object)) return null;
    return { code: 'READ_ONLY', message: `"${object.name}" is read-only in this editor`, object };
}

/** What {@link copyForInsideEdit} did. */
export interface InsideEditCopy {
    /**
     * Each copied placement, and the object standing where it stood: the
     * placement itself when the unpack keeps it, else the child it collapsed
     * into.
     */
    copied: Map<SceneObject, SceneObject>;
    /** Files the copy could not bring into the folder: the content renders, and a save cannot carry them. */
    orphaned: string[];
    /** One undo entry for every copy, already applied; null when nothing needed copying. */
    command: UndoCommand | null;
}

/**
 * Copy every placed reference an edit to `targets` would land inside, so the
 * edit can then be applied to nodes the scene owns. Several targets inside one
 * placement copy it once, and a target inside nested references copies each
 * of them, outermost first.
 *
 * Targets inside a placement keep their identity: the unpack moves the
 * placement's children out and keeps their world pose, so the caller applies
 * its edit to the same objects afterwards. A target that is itself a copied
 * placement may be replaced by its child; `copied` maps it to what now stands
 * in its place.
 *
 * Refuses a target the host holds read-only (`READ_ONLY`), and a copy with no
 * `ports.folder` (`NO_FOLDER`), before changing anything. A copy the unpack refuses (`REPARENT_REFUSED`) or a fault mid-way
 * takes back the copies already made before it returns or throws. Two things
 * are not taken back: files already uploaded into the folder, and the
 * placement whose unpack itself threw, which stays as the unpack left it. If
 * taking a copy back fails too, it throws an `AggregateError` holding the
 * cause first.
 */
export async function copyForInsideEdit(
    targets: SceneObject | readonly SceneObject[],
    ports: AuthoringPorts = {},
): Promise<InsideEditCopy | { refused: Refusal }> {
    const list: readonly SceneObject[] = Array.isArray(targets) ? targets : [targets as SceneObject];
    for (const target of list) {
        const readOnly = readOnlyRefusal(target, ports);
        if (readOnly) return { refused: readOnly };
    }

    const outermost = (): Set<SceneObject> => {
        const placements = new Set<SceneObject>();
        for (const target of list) {
            const placement = referenceCopyTarget(target);
            if (placement) placements.add(placement);
        }
        return placements;
    };
    let placements = outermost();
    if (placements.size === 0) return { copied: new Map(), orphaned: [], command: null };

    const [first] = placements;
    const folder = ports.folder;
    if (!folder) {
        return { refused: {
            code: 'NO_FOLDER',
            message: `"${first.name}" is a placed reference, and there is no scene folder to copy it into`,
            object: first,
        } };
    }

    const done: UndoCommand[] = [];
    const copied = new Map<SceneObject, SceneObject>();
    const orphaned: string[] = [];
    let refusal: { refused: Refusal } | null = null;
    try {
        // Each round copies the outermost placements left; a copy brings the
        // references nested in it to the top, where the next round finds them.
        // A round always unpacks or stops, so this ends.
        rounds: while (placements.size > 0) {
            for (const placement of placements) {
                const step = await copyReference(placement, folder);
                if ('refused' in step) {
                    refusal = step;
                    break rounds;
                }
                done.push(step.command);
                copied.set(placement, step.object);
                orphaned.push(...step.orphaned);
            }
            placements = outermost();
        }
    } catch (error) {
        await rollBack(done, error);
        throw error;
    }
    // Outside the try, so a rollback that fails here is not rolled back again.
    if (refusal) {
        await rollBack(done, refusal.refused);
        return refusal;
    }
    const command = done.length === 1 ? done[0] : new CompositeCommand('Copy references into the scene', done);
    return { copied, orphaned, command };
}

/**
 * Unpack one placement into the scene's folder, as one undo entry.
 *
 * The unpack is in place, so undo is the two halves of a swap: delete what
 * the unpack left, then rebuild the reference. `CompositeCommand` undoes in
 * reverse, which gives that order. Both commands resolve their objects
 * through the scene's identity registry, so they can name the same node.
 */
async function copyReference(
    placement: SceneObject,
    folder: UnpackFolderTarget,
): Promise<{ object: SceneObject; orphaned: string[]; command: UndoCommand } | { refused: Refusal }> {
    const name = placement.name;
    const subtree = [placement, ...placement.getDescendants()];
    const restore = new DeleteObjectCommand(
        placement,
        subtree.map(object => ({ object, data: capture(object), parent: object.getParent() })),
        placement.getParent(),
    );

    const result = await placement.traceScene.unpackReference(placement, { intoFolder: folder });
    const copy = result.object;
    if (!result.unpacked || !copy) {
        return { refused: {
            code: 'REPARENT_REFUSED',
            message: `"${name}" could not be copied into this scene: the unpack was refused`,
            object: placement,
        } };
    }

    const create = new CreateObjectCommand(
        copy,
        copy.getParent(),
        capture(copy),
        descendantEntries(copy),
    );
    return {
        object: copy,
        orphaned: result.orphaned,
        command: new CompositeCommand(`Copy ${name} to scene`, [restore, create]),
    };
}
